import { Fragment, useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts'
import { positionsApi } from '../utils/api'
import {
  formatProbability,
  formatEdge,
  formatPnL,
  formatQuantity,
  getEdgeClass,
  getActionBadgeClass,
  getThesisStatusBadgeClass,
  formatTimeCountdown,
  getTimeCountdownClass,
  formatPnLPercent,
  formatTimeAgo,
  getPositionStaleness,
  getStalenessBadgeClass,
} from '../utils/format'
import { PositionsSkeleton } from './Skeleton'
import clsx from 'clsx'

// Chart colors
const CATEGORY_COLORS = [
  '#00d4ff', // signal-blue
  '#00ff88', // signal-green
  '#ff6b6b', // signal-red
  '#ffd93d', // signal-amber
  '#a855f7', // signal-purple
  '#6366f1', // indigo
  '#14b8a6', // teal
  '#f97316', // orange
]

// Sortable column types
type SortColumn = 'edge' | 'pnl' | 'exposure' | 'close_time' | null
type SortDirection = 'asc' | 'desc'

// Sortable column header component
function SortableHeader({
  column,
  label,
  currentSort,
  currentDirection,
  onSort,
}: {
  column: SortColumn
  label: string
  currentSort: SortColumn
  currentDirection: SortDirection
  onSort: (col: SortColumn) => void
}) {
  const isActive = currentSort === column
  return (
    <th
      className="text-right p-4 text-xs font-mono text-star-muted font-normal cursor-pointer hover:text-star transition-colors"
      onClick={() => onSort(column)}
    >
      <span className="flex items-center justify-end gap-1">
        {label}
        {isActive && (
          <span className="text-signal-blue">
            {currentDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </span>
    </th>
  )
}

export function Positions() {
  const queryClient = useQueryClient()

  // Sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  const toggleSelection = (questionId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(questionId)) {
        next.delete(questionId)
      } else {
        next.add(questionId)
      }
      return next
    })
  }

  const toggleSelectAll = (questionIds: number[]) => {
    const allSelected = questionIds.every((id) => selectedIds.has(id))
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(questionIds))
    }
  }

  const clearSelection = () => {
    setSelectedIds(new Set())
  }

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumn(column)
      setSortDirection('desc')
    }
  }

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['positions'],
    queryFn: positionsApi.list,
    refetchInterval: 60000, // Refresh every minute
  })

  // Sort positions based on current sort column/direction
  const sortedPositions = useMemo(() => {
    if (!data?.positions || !sortColumn) return data?.positions

    return [...data.positions].sort((a, b) => {
      let aVal: number
      let bVal: number

      switch (sortColumn) {
        case 'edge':
          aVal = a.edge ?? 0
          bVal = b.edge ?? 0
          break
        case 'pnl':
          aVal = a.total_unrealized_pnl ?? 0
          bVal = b.total_unrealized_pnl ?? 0
          break
        case 'exposure':
          aVal = a.total_cost_basis ?? 0
          bVal = b.total_cost_basis ?? 0
          break
        case 'close_time':
          aVal = a.close_ts ? new Date(a.close_ts).getTime() : Infinity
          bVal = b.close_ts ? new Date(b.close_ts).getTime() : Infinity
          break
        default:
          return 0
      }

      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
    })
  }, [data?.positions, sortColumn, sortDirection])

  const runReview = useMutation({
    mutationFn: (questionId: number) => positionsApi.runReview(questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] })
    },
  })
  
  const runAllReviews = useMutation({
    mutationFn: positionsApi.runAllReviews,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] })
    },
  })
  
  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-display font-semibold text-star mb-1 sm:mb-2">
            Active Positions
          </h2>
          <p className="text-star-muted text-xs sm:text-sm">
            {data?.total_count ?? 0} positions tracked
          </p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            className="btn-secondary min-h-[44px] text-xs sm:text-sm"
            onClick={() => refetch()}
            aria-label="Refresh positions data"
          >
            <span aria-hidden="true">↻</span> Refresh
          </button>
          <button
            className="btn-primary min-h-[44px] text-xs sm:text-sm"
            onClick={() => runAllReviews.mutate()}
            disabled={runAllReviews.isPending}
            aria-label={runAllReviews.isPending ? 'Reviewing all positions...' : 'Review all positions'}
          >
            <span aria-hidden="true">{runAllReviews.isPending ? '⏳' : '🔍'}</span> Review All
          </button>
        </div>
      </div>
      
      {/* Portfolio summary */}
      {data && (
        <div className="glass-card p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div>
              <span className="data-label text-[11px] sm:text-xs">Total Exposure</span>
              <div className="data-value-lg text-xl sm:text-2xl">${data.total_exposure.toFixed(2)}</div>
            </div>
            <div>
              <span className="data-label text-[11px] sm:text-xs">Realized P&L</span>
              <div className={clsx(
                'data-value-lg text-xl sm:text-2xl',
                data.total_realized_pnl >= 0 ? 'text-signal-green' : 'text-signal-red'
              )}>
                {formatPnL(data.total_realized_pnl)}
              </div>
            </div>
            <div>
              <span className="data-label text-[11px] sm:text-xs">Unrealized P&L</span>
              <div className={clsx(
                'data-value-lg text-xl sm:text-2xl',
                data.total_unrealized_pnl >= 0 ? 'text-signal-green' : 'text-signal-red'
              )}>
                {formatPnL(data.total_unrealized_pnl)}
              </div>
            </div>
            <div>
              <span className="data-label text-[11px] sm:text-xs">Total P&L</span>
              <div className={clsx(
                'data-value-lg text-xl sm:text-2xl',
                (data.total_realized_pnl + data.total_unrealized_pnl) >= 0 ? 'text-signal-green' : 'text-signal-red'
              )}>
                {formatPnL(data.total_realized_pnl + data.total_unrealized_pnl)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Row - Category Exposure & P&L Distribution */}
      {data && data.positions.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
          {/* Category Exposure Pie Chart */}
          <div className="glass-card p-4">
            <h3 className="font-mono text-sm text-star-muted mb-4">Exposure by Category</h3>
            <CategoryExposureChart positions={data.positions} />
          </div>

          {/* P&L Distribution Bar Chart */}
          <div className="glass-card p-4">
            <h3 className="font-mono text-sm text-star-muted mb-4">P&L Distribution</h3>
            <PnLDistributionChart positions={data.positions} />
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && <PositionsSkeleton />}
      
      {/* Error state */}
      {error && (
        <div className="glass-card p-8 text-center">
          <p className="text-signal-red font-mono mb-4">Failed to load positions</p>
          <button className="btn-primary" onClick={() => refetch()}>
            Try Again
          </button>
        </div>
      )}
      
      {/* Positions - Mobile Cards + Desktop Table */}
      {data && data.positions.length > 0 && (
        <div className="glass-card overflow-hidden">
          {/* Mobile Sort Controls - with scroll snap for better UX */}
          <div className="md:hidden p-3 border-b border-star-dim/20 flex items-center gap-2 overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-thin scrollbar-thumb-star-dim/30">
            <span className="text-xs text-star-dim shrink-0 snap-start">Sort:</span>
            {(['edge', 'pnl', 'exposure', 'close_time'] as const).map((col) => (
              <button
                key={col}
                onClick={() => handleSort(col)}
                className={clsx(
                  'px-3 py-1.5 rounded-full text-xs font-mono whitespace-nowrap min-h-[44px] snap-start',
                  sortColumn === col
                    ? 'bg-signal-blue/20 text-signal-blue border border-signal-blue/40'
                    : 'bg-void-100 text-star-muted border border-star-dim/30'
                )}
              >
                {col === 'close_time' ? 'Closes' : col.charAt(0).toUpperCase() + col.slice(1)}
                {sortColumn === col && (sortDirection === 'desc' ? ' ↓' : ' ↑')}
              </button>
            ))}
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-star-dim/10">
            <AnimatePresence>
              {(sortedPositions || []).map((position, index) => {
                const countdown = formatTimeCountdown(position.close_ts)
                return (
                  <motion.div
                    key={position.question_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className={clsx(
                      selectedIds.has(position.question_id) && 'bg-signal-blue/5'
                    )}
                  >
                    <div className="p-4">
                      {/* Selection checkbox and title */}
                      <div className="flex items-start gap-3 mb-3">
                        <input
                          type="checkbox"
                          className="w-5 h-5 mt-0.5 rounded border-star-dim/50 bg-void-100 text-signal-blue focus:ring-signal-blue/30 cursor-pointer shrink-0"
                          checked={selectedIds.has(position.question_id)}
                          onChange={() => toggleSelection(position.question_id)}
                          aria-label={`Select ${position.question_title}`}
                        />
                        <Link
                          to={`/question/${position.question_id}`}
                          className="flex-1 min-w-0"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-star line-clamp-2">
                                {position.question_title}
                              </div>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {position.category && (
                                  <span className="text-xs text-star-muted">{position.category}</span>
                                )}
                                {position.child_positions.length > 0 && (
                                  <span className="badge-blue text-[10px]">
                                    {position.child_positions.length} markets
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* P&L badge */}
                            <div className={clsx(
                              'text-sm font-mono font-semibold shrink-0',
                              (position.total_unrealized_pnl ?? 0) >= 0 ? 'text-signal-green' : 'text-signal-red'
                            )}>
                              {formatPnL(position.total_unrealized_pnl)}
                            </div>
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-4 gap-2 text-center mb-3">
                            <div>
                              <div className="text-[11px] text-star-dim mb-0.5">Ours</div>
                              <div className="font-mono text-xs text-star">{formatProbability(position.our_probability)}</div>
                            </div>
                            <div>
                              <div className="text-[11px] text-star-dim mb-0.5">Market</div>
                              <div className="font-mono text-xs text-star-muted">{formatProbability(position.market_probability)}</div>
                            </div>
                            <div>
                              <div className="text-[11px] text-star-dim mb-0.5">Edge</div>
                              <div className={clsx('font-mono text-xs font-semibold', getEdgeClass(position.edge))}>
                                {formatEdge(position.edge)}
                              </div>
                            </div>
                            <div>
                              <div className="text-[11px] text-star-dim mb-0.5">Exposure</div>
                              <div className="font-mono text-xs text-star">${position.total_cost_basis.toFixed(0)}</div>
                            </div>
                          </div>

                          {/* Bottom row: Status + Closes */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                              {position.last_review ? (
                                <>
                                  <span className={clsx(getActionBadgeClass(position.last_review.action), 'text-[10px]')}>
                                    {position.last_review.action.toUpperCase()}
                                  </span>
                                  <span className={clsx(getThesisStatusBadgeClass(position.last_review.thesis_status), 'text-[10px]')}>
                                    {position.last_review.thesis_status}
                                  </span>
                                  {(() => {
                                    const staleness = getPositionStaleness(position.last_review.created_at)
                                    if (staleness && staleness.level !== 'fresh') {
                                      return (
                                        <span className={clsx('text-[9px] px-1 py-0.5 rounded border', getStalenessBadgeClass(staleness.level))}>
                                          {staleness.level === 'critical' ? '⚠️' : ''}{staleness.label}
                                        </span>
                                      )
                                    }
                                    return null
                                  })()}
                                </>
                              ) : (
                                <span className="text-[10px] px-1 py-0.5 rounded bg-signal-red/10 text-signal-red border border-signal-red/30">
                                  ⚠️ No review
                                </span>
                              )}
                            </div>
                            <span className={clsx('font-mono text-xs', getTimeCountdownClass(countdown.urgency))}>
                              {countdown.text}
                              {countdown.urgency === 'critical' && ' ⚠️'}
                            </span>
                          </div>
                        </Link>
                      </div>

                      {/* Review button */}
                      <div className="pt-2">
                        <button
                          className="w-full px-3 py-2 rounded bg-signal-blue/10 text-signal-blue border border-signal-blue/30 text-xs font-mono hover:bg-signal-blue/20 transition-colors min-h-[44px]"
                          onClick={(e) => {
                            e.preventDefault()
                            runReview.mutate(position.question_id)
                          }}
                          disabled={runReview.isPending}
                        >
                          {runReview.isPending ? '⏳' : '🔍'} Review Position
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </div>

          {/* Bulk Action Bar */}
          {selectedIds.size > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 px-4 py-3 bg-signal-blue/10 border border-signal-blue/30 rounded-lg flex flex-wrap items-center gap-3"
            >
              <span className="text-star text-sm font-medium">
                {selectedIds.size} position{selectedIds.size !== 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1.5 bg-signal-blue text-white rounded text-xs font-medium hover:bg-signal-blue/80 transition-colors"
                  onClick={() => {
                    // Run review on all selected positions
                    selectedIds.forEach((id) => {
                      runReview.mutate(id)
                    })
                  }}
                  disabled={runReview.isPending}
                >
                  {runReview.isPending ? '⏳ Reviewing...' : '🔍 Review Selected'}
                </button>
                <button
                  className="px-3 py-1.5 bg-void-100 text-star-muted rounded text-xs font-medium hover:bg-void-200 transition-colors"
                  onClick={clearSelection}
                >
                  Clear Selection
                </button>
              </div>
            </motion.div>
          )}

          {/* Desktop Table View */}
          <div className="hidden md:block">
            <table className="w-full">
              <thead>
                <tr className="border-b border-star-dim/20">
                  <th className="w-12 p-4">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-star-dim/50 bg-void-100 text-signal-blue focus:ring-signal-blue/30 cursor-pointer"
                      checked={(sortedPositions?.length ?? 0) > 0 && (sortedPositions ?? []).every((p) => selectedIds.has(p.question_id))}
                      onChange={() => toggleSelectAll(sortedPositions?.map((p) => p.question_id) ?? [])}
                      aria-label="Select all positions"
                    />
                  </th>
                  <th className="text-left p-4 text-xs font-mono text-star-muted font-normal">Event</th>
                  <th className="text-right p-4 text-xs font-mono text-star-muted font-normal">Our Prob</th>
                  <th className="text-right p-4 text-xs font-mono text-star-muted font-normal">Market</th>
                  <SortableHeader
                    column="edge"
                    label="Edge"
                    currentSort={sortColumn}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    column="exposure"
                    label="Exposure"
                    currentSort={sortColumn}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    column="pnl"
                    label="P&L"
                    currentSort={sortColumn}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    column="close_time"
                    label="Closes"
                    currentSort={sortColumn}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <th className="text-center p-4 text-xs font-mono text-star-muted font-normal">Status</th>
                  <th className="text-center p-4 text-xs font-mono text-star-muted font-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {(sortedPositions || []).map((position, index) => (
                    <Fragment key={position.question_id}>
                      {/* Event row */}
                      <motion.tr
                        key={`event-${position.question_id}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className={clsx(
                          'border-b border-star-dim/10 hover:bg-void-50/50',
                          selectedIds.has(position.question_id) && 'bg-signal-blue/5'
                        )}
                      >
                        {/* Checkbox */}
                        <td className="p-4">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-star-dim/50 bg-void-100 text-signal-blue focus:ring-signal-blue/30 cursor-pointer"
                            checked={selectedIds.has(position.question_id)}
                            onChange={() => toggleSelection(position.question_id)}
                            aria-label={`Select ${position.question_title}`}
                          />
                        </td>
                        {/* Event */}
                        <td className="p-4">
                          <Link
                            to={`/question/${position.question_id}`}
                            className="text-star hover:text-signal-blue transition-colors block"
                          >
                            <div className="font-medium text-sm line-clamp-2">
                              {position.question_title}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {position.category && (
                                <span className="text-xs text-star-muted">
                                  {position.category}
                                </span>
                              )}
                              {position.child_positions.length > 0 && (
                                <span className="badge-blue text-[10px]">
                                  {position.child_positions.length} markets
                                </span>
                              )}
                            </div>
                          </Link>
                        </td>

                        {/* Our probability */}
                        <td className="p-4 text-right font-mono text-sm text-star">
                          {formatProbability(position.our_probability)}
                        </td>

                        {/* Market probability */}
                        <td className="p-4 text-right font-mono text-sm text-star">
                          {formatProbability(position.market_probability)}
                        </td>

                        {/* Edge */}
                        <td className={clsx(
                          'p-4 text-right font-mono text-sm font-semibold',
                          getEdgeClass(position.edge)
                        )}>
                          {formatEdge(position.edge)}
                        </td>

                        {/* Cost Basis */}
                        <td className="p-4 text-right font-mono text-sm text-star">
                          ${position.total_cost_basis.toFixed(2)}
                        </td>

                        {/* P&L with percentage */}
                        <td className="p-4 text-right">
                          <div className={clsx(
                            'font-mono text-sm font-semibold',
                            (position.total_unrealized_pnl ?? 0) >= 0 ? 'text-signal-green' : 'text-signal-red'
                          )}>
                            {formatPnL(position.total_unrealized_pnl)}
                          </div>
                          <div className="text-xs text-star-dim font-mono">
                            {formatPnLPercent(position.total_unrealized_pnl, position.total_cost_basis)}
                          </div>
                        </td>

                        {/* Time to close */}
                        <td className="p-4 text-right">
                          {(() => {
                            const countdown = formatTimeCountdown(position.close_ts)
                            return (
                              <span className={clsx('font-mono text-sm', getTimeCountdownClass(countdown.urgency))}>
                                {countdown.text}
                                {countdown.urgency === 'critical' && ' ⚠️'}
                              </span>
                            )
                          })()}
                        </td>

                        {/* Last review status with staleness */}
                        <td className="p-4 text-center">
                          {position.last_review ? (
                            <div className="flex flex-col items-center gap-1">
                              <span className={getActionBadgeClass(position.last_review.action)}>
                                {position.last_review.action.toUpperCase()}
                              </span>
                              <span className={getThesisStatusBadgeClass(position.last_review.thesis_status)}>
                                {position.last_review.thesis_status}
                              </span>
                              {(() => {
                                const staleness = getPositionStaleness(position.last_review.created_at)
                                if (staleness && staleness.level !== 'fresh') {
                                  return (
                                    <span className={clsx('text-[9px] px-1.5 py-0.5 rounded border', getStalenessBadgeClass(staleness.level))}>
                                      {staleness.level === 'critical' ? '⚠️ ' : ''}{staleness.label}
                                    </span>
                                  )
                                }
                                return null
                              })()}
                            </div>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded text-[10px] bg-signal-red/10 text-signal-red border border-signal-red/30">
                              ⚠️ No review
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="p-4 text-center">
                          <button
                            className="px-2 py-1 rounded bg-signal-blue/10 text-signal-blue border border-signal-blue/30 text-xs font-mono hover:bg-signal-blue/20 transition-colors"
                            onClick={() => runReview.mutate(position.question_id)}
                            disabled={runReview.isPending}
                          >
                            {runReview.isPending ? '⏳' : '🔍'} Review
                          </button>
                        </td>
                      </motion.tr>

                      {/* Child market rows */}
                      {position.child_positions.map((child) => (
                        <motion.tr
                          key={`child-${child.question_id}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="bg-void-50/30 border-b border-star-dim/5"
                        >
                          <td className="pl-8 pr-4 py-2">
                            <span
                              className="text-xs text-star-muted"
                              title={child.native_id || child.title}
                            >
                              └ {child.outcome_name || child.title}
                            </span>
                          </td>
                          <td className="p-2 text-right font-mono text-xs text-star-dim">
                            {formatQuantity(child.yes_qty)} shares
                          </td>
                          <td className="p-2 text-right font-mono text-xs text-star-dim">
                            {child.avg_cost !== undefined ? `${(child.avg_cost * 100).toFixed(1)}¢` : '—'}
                          </td>
                          <td className="p-2 text-right font-mono text-xs text-star-dim">
                            {child.current_price !== undefined ? `${(child.current_price * 100).toFixed(1)}¢` : '—'}
                          </td>
                          <td className="p-2 text-right font-mono text-xs text-star-dim">
                            ${(child.yes_qty * child.avg_cost).toFixed(2)}
                          </td>
                          <td className={clsx(
                            'p-2 text-right font-mono text-xs',
                            (child.unrealized_pnl ?? 0) >= 0 ? 'text-signal-green' : 'text-signal-red'
                          )}>
                            {formatPnL(child.unrealized_pnl)}
                          </td>
                          <td></td>
                          <td></td>
                          <td></td>
                        </motion.tr>
                      ))}
                    </Fragment>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Empty state */}
      {data && data.positions.length === 0 && (
        <div className="glass-card p-12 text-center">
          <p className="text-star-muted font-mono text-lg mb-2">No active positions</p>
          <p className="text-star-dim text-sm">Positions will appear here when you trade</p>
        </div>
      )}

      {/* Closed Positions Section */}
      {data && data.closed_positions && data.closed_positions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-display font-semibold text-star">
              Recently Closed Positions
            </h3>
            {data.total_closed_pnl !== undefined && (
              <span className={clsx(
                'font-mono text-sm',
                data.total_closed_pnl >= 0 ? 'text-signal-green' : 'text-signal-red'
              )}>
                Total: {data.total_closed_pnl >= 0 ? '+' : ''}${data.total_closed_pnl.toFixed(2)}
              </span>
            )}
          </div>
          <div className="glass-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-star-dim/20">
                  <th className="text-left p-4 text-xs font-mono text-star-muted font-normal">Market</th>
                  <th className="text-center p-4 text-xs font-mono text-star-muted font-normal">Resolution</th>
                  <th className="text-right p-4 text-xs font-mono text-star-muted font-normal">Cost Basis</th>
                  <th className="text-right p-4 text-xs font-mono text-star-muted font-normal">P&L</th>
                  <th className="text-right p-4 text-xs font-mono text-star-muted font-normal">ROI</th>
                  <th className="text-right p-4 text-xs font-mono text-star-muted font-normal">Settled</th>
                </tr>
              </thead>
              <tbody>
                {data.closed_positions.map((pos, index) => (
                  <motion.tr
                    key={pos.question_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.35 + index * 0.02 }}
                    className="border-b border-star-dim/5 hover:bg-void-50/50"
                  >
                    <td className="p-4">
                      <Link
                        to={`/question/${pos.question_id}`}
                        className="text-star hover:text-signal-blue transition-colors"
                      >
                        <div className="font-medium text-sm line-clamp-2">
                          {pos.question_title}
                        </div>
                        {pos.category && (
                          <span className="text-xs text-star-muted">{pos.category}</span>
                        )}
                      </Link>
                    </td>
                    <td className="p-4 text-center">
                      <span className={clsx(
                        'px-2 py-1 rounded text-xs font-mono',
                        pos.resolution === 'Yes' && 'bg-signal-green/10 text-signal-green',
                        pos.resolution === 'No' && 'bg-signal-red/10 text-signal-red',
                        !pos.resolution && 'bg-star-dim/10 text-star-dim'
                      )}>
                        {pos.resolution || '—'}
                      </span>
                    </td>
                    <td className="p-4 text-right font-mono text-sm text-star-muted">
                      ${pos.cost_basis.toFixed(2)}
                    </td>
                    <td className={clsx(
                      'p-4 text-right font-mono text-sm font-semibold',
                      pos.realized_pnl >= 0 ? 'text-signal-green' : 'text-signal-red'
                    )}>
                      {pos.realized_pnl >= 0 ? '+' : ''}${pos.realized_pnl.toFixed(2)}
                    </td>
                    <td className={clsx(
                      'p-4 text-right font-mono text-sm',
                      pos.roi_percent && pos.roi_percent >= 0 ? 'text-signal-green' : 'text-signal-red'
                    )}>
                      {pos.roi_percent !== null ? `${pos.roi_percent >= 0 ? '+' : ''}${pos.roi_percent.toFixed(1)}%` : '—'}
                    </td>
                    <td className="p-4 text-right font-mono text-xs text-star-dim">
                      {pos.settled_at ? formatTimeAgo(pos.settled_at) : '—'}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </div>
  )
}

// Category Exposure Pie Chart
function CategoryExposureChart({ positions }: { positions: any[] }) {
  const categoryData = useMemo(() => {
    const categories: Record<string, { value: number; count: number }> = {}
    positions.forEach((pos) => {
      const cat = pos.category || 'Other'
      if (!categories[cat]) {
        categories[cat] = { value: 0, count: 0 }
      }
      categories[cat].value += pos.total_cost_basis || 0
      categories[cat].count += 1
    })

    return Object.entries(categories)
      .map(([name, data]) => ({ name, value: data.value, count: data.count }))
      .sort((a, b) => b.value - a.value)
  }, [positions])

  if (categoryData.length === 0) {
    return <div className="h-32 flex items-center justify-center text-star-dim text-xs">No data</div>
  }

  return (
    <div className="flex items-center gap-4">
      <div className="w-32 h-32">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={categoryData}
              cx="50%"
              cy="50%"
              innerRadius={25}
              outerRadius={50}
              paddingAngle={2}
              dataKey="value"
            >
              {categoryData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              content={({ payload }) => {
                if (!payload?.[0]) return null
                const data = payload[0].payload
                const totalExposure = categoryData.reduce((sum, c) => sum + c.value, 0)
                const percentage = totalExposure > 0 ? (data.value / totalExposure * 100).toFixed(1) : '0'
                return (
                  <div className="glass-card p-3 text-xs font-mono shadow-lg min-w-[140px] sm:min-w-[160px]">
                    <div className="text-star font-semibold mb-2">{data.name}</div>
                    <div className="space-y-1">
                      <div className="flex justify-between gap-4">
                        <span className="text-star-dim">Exposure</span>
                        <span className="text-star">${data.value.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-star-dim">Portfolio %</span>
                        <span className="text-signal-blue">{percentage}%</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-star-dim">Positions</span>
                        <span className="text-star">{data.count || '—'}</span>
                      </div>
                    </div>
                  </div>
                )
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex-1 space-y-1">
        {categoryData.slice(0, 5).map((cat, i) => (
          <div key={cat.name} className="flex items-center gap-2 text-xs">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}
            />
            <span className="text-star-muted flex-1 truncate">{cat.name}</span>
            <span className="text-star font-mono">${cat.value.toFixed(0)}</span>
          </div>
        ))}
        {categoryData.length > 5 && (
          <div className="text-[10px] text-star-dim">
            +{categoryData.length - 5} more categories
          </div>
        )}
      </div>
    </div>
  )
}

// P&L Distribution Bar Chart
function PnLDistributionChart({ positions }: { positions: any[] }) {
  const pnlData = useMemo(() => {
    return positions
      .map((pos) => {
        const costBasis = pos.total_cost_basis || 0
        const pnl = pos.total_unrealized_pnl || 0
        const roi = costBasis > 0 ? (pnl / costBasis * 100) : 0
        return {
          name: pos.question_title?.substring(0, 20) + '...' || 'Unknown',
          fullName: pos.question_title || 'Unknown',
          pnl,
          isPositive: pnl >= 0,
          costBasis,
          currentValue: costBasis + pnl,
          roi,
          edge: pos.current_edge,
          side: pos.side,
          quantity: pos.total_quantity,
        }
      })
      .sort((a, b) => b.pnl - a.pnl)
      .slice(0, 8) // Top 8 positions by P&L
  }, [positions])

  if (pnlData.length === 0) {
    return <div className="h-32 flex items-center justify-center text-star-dim text-xs">No data</div>
  }

  return (
    <div className="h-32">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={pnlData} layout="vertical" margin={{ left: 0, right: 10 }}>
          <XAxis
            type="number"
            tickFormatter={(v) => `$${v}`}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#555566', fontSize: 10, fontFamily: 'monospace' }}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={80}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#555566', fontSize: 9, fontFamily: 'monospace' }}
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload?.[0]) return null
              const data = payload[0].payload
              return (
                <div className="glass-card p-3 text-xs font-mono shadow-lg min-w-[180px] sm:min-w-[200px] max-w-[280px]">
                  <div className="text-star font-semibold mb-2 line-clamp-2">{data.fullName}</div>
                  <div className="space-y-1">
                    <div className="flex justify-between gap-4">
                      <span className="text-star-dim">P&L</span>
                      <span className={data.pnl >= 0 ? 'text-signal-green' : 'text-signal-red'}>
                        {formatPnL(data.pnl)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-star-dim">ROI</span>
                      <span className={data.roi >= 0 ? 'text-signal-green' : 'text-signal-red'}>
                        {data.roi >= 0 ? '+' : ''}{data.roi.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-star-dim">Cost Basis</span>
                      <span className="text-star">${data.costBasis.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-star-dim">Current Value</span>
                      <span className="text-star">${data.currentValue.toFixed(2)}</span>
                    </div>
                    {data.edge !== null && data.edge !== undefined && (
                      <div className="flex justify-between gap-4">
                        <span className="text-star-dim">Current Edge</span>
                        <span className={data.edge >= 0 ? 'text-signal-green' : 'text-signal-red'}>
                          {formatEdge(data.edge)}
                        </span>
                      </div>
                    )}
                    {data.side && (
                      <div className="flex justify-between gap-4">
                        <span className="text-star-dim">Side</span>
                        <span className={data.side === 'YES' ? 'text-signal-green' : 'text-signal-red'}>
                          {data.side}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )
            }}
          />
          <Bar
            dataKey="pnl"
            radius={[0, 4, 4, 0]}
          >
            {pnlData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isPositive ? '#00ff88' : '#ff6b6b'}
                fillOpacity={0.7}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
